import { getApiUrl, IGroundTruthJob } from "@app/shared";
import { useSignalr } from "@known-as-bmf/react-signalr";
import { useCallback, useEffect } from "react";
import { Subscription } from "rxjs";

const GROUND_TRUTH_JOBS_EVENT = "ReceiveGroundTruthJobs";
const WATCH_GROUND_TRUTH_COMMAND = "WatchGroundTruthJobs";

/**
 * Live ground truth job status for one facility.
 *
 * A run takes tens of seconds — the inference Lambda's cold start alone can be 42s — so the upload
 * returns as soon as the photos are stored and the counting happens in the background. This is how
 * the table learns the run moved to Stitching, then Complete or Failed, without the page polling
 * or the user sitting on a spinner.
 *
 * The server pushes the facility's whole job list on every change rather than a delta, so the
 * handler can replace its state outright and never has to reconcile.
 */
export function useGroundTruthHub(
    facilityId: string | undefined,
    onJobsReceived: (jobs: IGroundTruthJob[]) => void,
    unsubscribe?: boolean,
) {
    const { send, on } = useSignalr(getApiUrl(`/hubs/groundtruth`));

    const handleJobsReceived = useCallback((jobs: IGroundTruthJob[]) => {
        onJobsReceived(jobs);
    }, [onJobsReceived]);

    useEffect(() => {
        if (facilityId) {
            send(WATCH_GROUND_TRUTH_COMMAND, facilityId);
        }
    }, [facilityId, send]);

    useEffect(() => {
        let subscription: Subscription | undefined;

        if (!unsubscribe) {
            subscription = on<IGroundTruthJob[]>(GROUND_TRUTH_JOBS_EVENT).subscribe(handleJobsReceived);
        }

        return () => {
            subscription?.unsubscribe();
        };
    }, [on, unsubscribe, handleJobsReceived]);
}
